import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantSsoService } from '../../services/tenant-sso.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantAzureAdConfig } from '../../models/tenant-sso.model';
import { LoaderComponent } from '../../common/loader/loader.component';

interface SsoField {
  key: 'tenantId' | 'clientId' | 'clientSecret' | 'redirectUrl' | 'postLogoutUrl';
  label: string;
  icon: string;
  type?: string;
  helpText: string;
}

interface SsoGroup {
  title: string;
  icon: string;
  fields: SsoField[];
}

@Component({
  selector: 'app-tenant-sso',
  imports: [FormsModule, LoaderComponent],
  templateUrl: './tenant-sso.component.html',
  styleUrl: './tenant-sso.component.scss'
})
export class TenantSsoComponent implements OnInit {
  private tenantUid: string;

  isEditing = false;
  showSecret = false;
  isConfigured = false;
  loading = true;
  saving = false;

  groups: SsoGroup[] = [
    {
      title: 'Application Credentials',
      icon: 'bi-key-fill',
      fields: [
        { key: 'tenantId', label: 'Tenant ID', icon: 'bi-fingerprint', helpText: 'Unique identifier for this tenant in the identity provider.' },
        { key: 'clientId', label: 'Client ID', icon: 'bi-person-badge', helpText: 'Public identifier issued to the registered application.' },
        { key: 'clientSecret', label: 'Client Secret', icon: 'bi-shield-lock-fill', type: 'password', helpText: 'Confidential key used to authenticate the application.' }
      ]
    },
    {
      title: 'Redirect URLs',
      icon: 'bi-signpost-split-fill',
      fields: [
        { key: 'redirectUrl', label: 'Redirect URL', icon: 'bi-box-arrow-in-right', helpText: 'Where users land after a successful sign-in.' },
        { key: 'postLogoutUrl', label: 'Post Logout URL', icon: 'bi-box-arrow-right', helpText: 'Where users land after signing out.' }
      ]
    }
  ];

  sso: Record<SsoField['key'], string> = {
    tenantId: '',
    clientId: '',
    clientSecret: '',
    redirectUrl: '',
    postLogoutUrl: ''
  };

  // Secret as last returned by the API (masked) — used to detect whether the user actually typed a new one.
  private lastLoadedSecret = '';
  private snapshot: Record<SsoField['key'], string> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tenantSsoService: TenantSsoService,
    private toastrService: ToastrService
  ) {
    this.tenantUid = this.route.snapshot.paramMap.get('slug') ?? '';
  }

  ngOnInit(): void {
    this.load();
  }

  get allFields(): SsoField[] {
    return this.groups.flatMap(g => g.fields);
  }

  goBack(): void {
    this.router.navigate(['/tenants', this.tenantUid]);
  }

  toggleEdit(): void {
    if (!this.isEditing) {
      // Entering edit mode: remember current values so Cancel can restore them.
      this.snapshot = { ...this.sso };
      this.isEditing = true;
      return;
    }

    // Cancelling: revert to the last loaded values.
    if (this.snapshot) {
      this.sso = { ...this.snapshot };
      this.snapshot = null;
    }
    this.isEditing = false;
  }

  save(): void {
    this.saving = true;

    const payload = {
      azure_tenant_id: this.sso.tenantId,
      azure_client_id: this.sso.clientId,
      redirect_uri: this.sso.redirectUrl,
      post_logout_redirect_uri: this.sso.postLogoutUrl,
      // Only send the secret if the user actually changed it — the API returns it masked, so
      // re-sending an untouched masked value would overwrite the real secret with garbage.
      ...(this.sso.clientSecret !== this.lastLoadedSecret ? { azure_client_secret: this.sso.clientSecret } : {})
    };

    this.tenantSsoService.saveAzureAdConfig(this.tenantUid, payload).subscribe({
      next: (config) => {
        this.applyConfig(config);
        this.saving = false;
        this.isEditing = false;
        this.snapshot = null;
        this.toastrService.success('SSO settings saved successfully.');
      },
      error: () => {
        this.saving = false;
        this.toastrService.error('Unable to save SSO settings.');
      }
    });
  }

  private load(): void {
    this.loading = true;

    this.tenantSsoService.getAzureAdConfig(this.tenantUid).subscribe({
      next: (config) => {
        this.applyConfig(config);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          // No SSO config yet for this tenant — not an error, just an empty form.
          this.isConfigured = false;
        } else {
          this.toastrService.error('Unable to load SSO settings.');
        }
      }
    });
  }

  private applyConfig(config: TenantAzureAdConfig): void {
    this.sso = {
      tenantId: config.azure_tenant_id ?? '',
      clientId: config.azure_client_id ?? '',
      clientSecret: config.azure_client_secret ?? '',
      redirectUrl: config.redirect_uri ?? '',
      postLogoutUrl: config.post_logout_redirect_uri ?? ''
    };
    this.lastLoadedSecret = this.sso.clientSecret;
    this.isConfigured = true;
  }
}
